import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  useNavigate,
  useParams,
  useLocation
} from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Check,
  Play,
  Circle
} from "lucide-react";

import {
  getSectionProgress
} from "../../api/syllabus/syllabusApi";
import {
  getSectionProgressPortal,
  getMySyllabus,
  toggleTopicStatus
} from "../../api/syllabus/syllabusPortalApi";
import {
  useToast
} from "../../context/ToastContext";

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isOverdue = (date) => !!date && new Date(date) < new Date();

const getNextStatus = (currentStatus) => {
  if (currentStatus === "PENDING") return "IN_PROGRESS";
  if (currentStatus === "IN_PROGRESS") return "COVERED";
  if (currentStatus === "COVERED") return "PENDING";
  return "IN_PROGRESS";
};

const buildProgressMap = (progressChapters) => {
  const map = {};
  progressChapters.forEach((chapter) => {
    chapter.topics.forEach((topic) => {
      map[`${chapter.chapterNo}-${topic.topicNo}`] = {
        status: topic.status,
        coveredOn: topic.coveredOn,
        inProgressOn: topic.inProgressOn,
      };
    });
  });
  return map;
};

const calculateSummary = (progressChapters, progressMap) => {
  let totalTopics = 0;
  let covered = 0;
  let inProgress = 0;
  let pending = 0;
  let overdue = 0;
  const today = new Date();

  progressChapters.forEach((chapter) => {
    chapter.topics.forEach((topic) => {
      totalTopics++;
      const record = progressMap[`${chapter.chapterNo}-${topic.topicNo}`];
      const status = record?.status || "PENDING";
      if (status === "COVERED") covered++;
      else if (status === "IN_PROGRESS") inProgress++;
      else pending++;

      if (status !== "COVERED" && topic.targetCompletionDate && new Date(topic.targetCompletionDate) < today) {
        overdue++;
      }
    });
  });

  return {
    totalTopics,
    covered,
    inProgress,
    pending,
    overdue,
    completionPercentage: totalTopics > 0 ? Math.round((covered / totalTopics) * 100) : 0,
  };
};

const SyllabusProgress = ({ isPortal = false }) => {
  const { templateId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const sectionId = new URLSearchParams(search).get("sectionId");
  const subjectIdParam = new URLSearchParams(search).get("subjectId");
  const useMySyllabus = new URLSearchParams(search).get("mySyllabus") === "true";

  const [templateData, setTemplateData] = useState(null); // { template, progress, summary }
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const [expanded, setExpanded] = useState(new Set());
  const [toggling, setToggling] = useState(null); // "chapterNo-topicNo" currently in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (useMySyllabus) {
        res = await getMySyllabus(sectionId, subjectIdParam);
      } else if (isPortal) {
        res = await getSectionProgressPortal(sectionId, subjectIdParam || "");
      } else {
        res = await getSectionProgress(sectionId, subjectIdParam || "");
      }

      const data = res.data || res;

      // getSectionProgress (owner/portal) returns an array of { template, progress, summary }
      // filtered down to the one matching this templateId; getMySyllabus returns a single object.
      let entry;
      if (Array.isArray(data)) {
        entry = data.find((d) => d.template?._id === templateId) || data[0];
      } else {
        entry = data;
      }

      if (!entry) {
        toast.error("No syllabus data found for this section");
        return;
      }

      setTemplateData(entry);
      const map = buildProgressMap(entry.progress || []);
      setProgressMap(map);

      if (entry.progress?.length) {
        setExpanded(new Set([entry.progress[0].chapterNo]));
      }
    } catch (err) {
      console.error("Failed to load section progress:", err);
      toast.error(err?.response?.data?.message || "Failed to load syllabus progress");
    } finally {
      setLoading(false);
    }
  }, [isPortal, sectionId, subjectIdParam, templateId, useMySyllabus]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    if (!templateData) return null;
    return calculateSummary(templateData.progress || [], progressMap);
  }, [templateData, progressMap]);

  const toggleExpand = (chapterNo) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(chapterNo)) next.delete(chapterNo);
      else next.add(chapterNo);
      return next;
    });
  };

  const handleToggle = async (chapterNo, topicNo, currentStatus) => {
    const key = `${chapterNo}-${topicNo}`;
    const nextStatus = getNextStatus(currentStatus);
    const original = progressMap[key];

    // Optimistic update
    setProgressMap((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        status: nextStatus,
        coveredOn: nextStatus === "COVERED" ? new Date().toISOString() : null,
        inProgressOn:
          nextStatus === "IN_PROGRESS"
            ? prev[key]?.inProgressOn || new Date().toISOString()
            : nextStatus === "PENDING"
            ? null
            : prev[key]?.inProgressOn,
      },
    }));

    setToggling(key);
    try {
      await toggleTopicStatus({
        syllabusTemplateId: templateId,
        sectionId,
        chapterNo,
        topicNo,
        status: nextStatus,
      });
    } catch (err) {
      console.error("Failed to update topic status:", err);
      // Revert on failure
      setProgressMap((prev) => ({ ...prev, [key]: original }));
      toast.error("Failed to update topic status");
    } finally {
      setToggling(null);
    }
  };

  if (!sectionId) {
    return (
      <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">
        A section must be selected to view syllabus progress.
      </div>
    );
  }

  if (loading) {
    return <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">Loading progress…</div>;
  }

  if (!templateData) {
    return <div className="text-center text-neutral-400 dark:text-neutral-500 py-16">No data found</div>;
  }

  const { template, section, progress } = templateData;
  const subject = template.subjectId || {};
  const classInfo = template.classId || {};

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:text-neutral-100 mb-4"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span
            className="text-xs px-2 py-0.5 rounded-lg text-white"
            style={{ backgroundColor: subject.color || "#8b5cf6" }}
          >
            {subject.name || "Subject"}
          </span>
          <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 text-xs px-2 py-0.5 rounded-lg">
            {classInfo.className || "Class"}
          </span>
          {section?.sectionName && (
            <span className="bg-violet-50 text-violet-700 text-xs px-2 py-0.5 rounded-lg font-medium">
              Section {section.sectionName}
            </span>
          )}
        </div>
        <h1 className="text-xl font-bold text-neutral-800 dark:text-neutral-100 mb-4">{template.title}</h1>

        

        <div className="flex gap-4 mb-4 flex-wrap">
          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
            {summary.covered} Covered
          </span>
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
            {summary.inProgress} In Progress
          </span>
          <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 px-3 py-1 rounded-full text-sm">
            {summary.pending} Pending
          </span>
          {summary.overdue > 0 && (
            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
              {summary.overdue} Overdue
            </span>
          )}
        </div>

        <div className="w-full bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 rounded-full h-3 mb-2">
          <div
            className="bg-green-500 h-3 rounded-full transition-all"
            style={{ width: `${summary.completionPercentage}%` }}
          />
        </div>
        <p className="text-right text-sm text-neutral-500 dark:text-neutral-400">{summary.completionPercentage}% Complete</p>
      </div>

      {/* Chapters + topics */}
      {(progress || []).map((chapter) => {
        const chapterExpanded = expanded.has(chapter.chapterNo);
        return (
          <div key={chapter.chapterNo} className="bg-white dark:bg-neutral-900 border rounded-2xl mb-4">
            <div
              className="flex justify-between items-center p-4 cursor-pointer"
              onClick={() => toggleExpand(chapter.chapterNo)}
            >
              <div className="flex items-center gap-3">
                {chapterExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className="bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 px-2 py-1 rounded-lg text-sm font-mono">
                  Ch {chapter.chapterNo}
                </span>
                <h3 className="font-bold">{chapter.chapterName}</h3>
              </div>
              {chapter.isOverdue && (
                <span className="text-xs text-red-500 font-medium">Overdue</span>
              )}
            </div>

            {chapterExpanded && (
              <div className="px-2 pb-2">
                {chapter.topics.map((topic) => {
                  const key = `${chapter.chapterNo}-${topic.topicNo}`;
                  const record = progressMap[key];
                  const status = record?.status || "PENDING";
                  const topicOverdue =
                    status !== "COVERED" && isOverdue(topic.targetCompletionDate);

                  return (
                    <div
                      key={topic.topicNo}
                      className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggle(chapter.chapterNo, topic.topicNo, status)}
                          disabled={toggling === key}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition ${ status === "COVERED" ? "bg-green-500 text-white" : status === "IN_PROGRESS" ? "bg-yellow-400 text-white" : "bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-400 dark:text-neutral-500 hover:bg-gray-200" } ${toggling === key ? "opacity-60" : ""}`}
                          title={status}
                        >
                          {status === "COVERED" ? (
                            <Check size={16} />
                          ) : status === "IN_PROGRESS" ? (
                            <Play size={14} />
                          ) : (
                            <Circle size={14} />
                          )}
                        </button>

                        <div>
                          <p
                            className={`text-sm font-medium ${ status === "COVERED" ? "line-through text-neutral-400 dark:text-neutral-500" : "" }`}
                          >
                            {topic.topicName}
                          </p>
                          {status === "COVERED" && record?.coveredOn && (
                            <p className="text-xs text-neutral-400 dark:text-neutral-500">
                              Covered on {formatDate(record.coveredOn)}
                            </p>
                          )}
                          {status === "IN_PROGRESS" && record?.inProgressOn && (
                            <p className="text-xs text-yellow-600">
                              Started on {formatDate(record.inProgressOn)}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-neutral-400 dark:text-neutral-500">
                        {topic.targetCompletionDate && (
                          <span className={topicOverdue ? "text-red-500" : ""}>
                            Due: {formatDate(topic.targetCompletionDate)}
                          </span>
                        )}
                        <span>{topic.estimatedPeriods} periods</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SyllabusProgress;